import { useEffect, useMemo, useState } from "react";
import Card, { Empty } from "./Card";
import { getCapacity } from "../../api/insights";

// One bus, so Fri/Sat/Sun are the inventory. Weeks run left to right, days top
// to bottom. Booked is a solid fill, held (a quote out, or a draft) is a dashed
// outline, so the states read without relying on color alone.

const MARK = "#2F8A57";
const DOWS = ["Fri", "Sat", "Sun"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const parse = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const STATUS_LABEL = { booked: "Booked", held: "Held — quote out", open: "Open" };

export default function WeekendGrid() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    getCapacity(8)
      .then(setData)
      .catch((e) => setError(e.message || "Could not load weekends."));
  }, []);

  const weeks = useMemo(() => {
    if (!data) return [];
    const out = [];
    for (let i = 0; i < data.days.length; i += 3) out.push(data.days.slice(i, i + 3));
    return out;
  }, [data]);

  const selected = data?.days.find((d) => d.date === picked) || null;
  const pct = data && data.total ? Math.round((data.booked / data.total) * 100) : 0;

  return (
    <Card
      title="Weekends, next 8 weeks"
      subtitle={
        data
          ? `${data.booked} of ${data.total} booked (${pct}%) · ${data.held} held by open quotes`
          : "Fri, Sat and Sun are what you sell"
      }
    >
      {error ? (
        <Empty>{error}</Empty>
      ) : !data ? (
        <div className="h-28" />
      ) : (
        <>
          <div className="grid gap-1" style={{ gridTemplateColumns: `28px repeat(${weeks.length}, minmax(0, 1fr))` }}>
            <div />
            {weeks.map((w, i) => {
              const fri = parse(w[0].date);
              const prev = i > 0 ? parse(weeks[i - 1][0].date) : null;
              const showMonth = !prev || prev.getMonth() !== fri.getMonth();
              return (
                <div key={w[0].date} className="text-center text-[10px] leading-3 text-birdie-charcoal/45 h-6">
                  {showMonth ? MONTHS[fri.getMonth()] : ""}
                </div>
              );
            })}

            {DOWS.map((dow, row) => (
              <Row key={dow} dow={dow}>
                {weeks.map((w) => {
                  const d = w[row];
                  return (
                    <DayCell
                      key={d.date}
                      day={d}
                      active={picked === d.date}
                      onClick={() => setPicked((p) => (p === d.date ? null : d.date))}
                    />
                  );
                })}
              </Row>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[11px] text-birdie-charcoal/60">
            <Legend swatch={<span className="w-3 h-3 rounded-[3px]" style={{ background: MARK }} />}>Booked</Legend>
            <Legend swatch={<span className="w-3 h-3 rounded-[3px] border-2 border-dashed" style={{ borderColor: MARK }} />}>
              Held
            </Legend>
            <Legend swatch={<span className="w-3 h-3 rounded-[3px] bg-birdie-charcoal/[0.06]" />}>Open</Legend>
          </div>

          <div className="mt-3 min-h-[44px] rounded-xl bg-birdie-cream/70 px-3 py-2 text-sm" aria-live="polite">
            {selected ? (
              <DayDetail day={selected} />
            ) : (
              <span className="text-birdie-charcoal/50 text-xs">Tap a day to see what's on it.</span>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

function Row({ dow, children }) {
  return (
    <>
      <div className="text-[10px] text-birdie-charcoal/45 flex items-center">{dow}</div>
      {children}
    </>
  );
}

function DayCell({ day, active, onClick }) {
  const n = parse(day.date).getDate();
  const style =
    day.status === "booked"
      ? { background: MARK, color: "#fff" }
      : day.status === "held"
      ? { border: `2px dashed ${MARK}`, color: MARK }
      : {};
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${day.dow} ${day.date}: ${STATUS_LABEL[day.status]}`}
      aria-pressed={active}
      className={`h-11 min-w-0 rounded-md text-[11px] font-semibold tabular-nums flex items-center justify-center transition ${
        day.status === "open" ? "bg-birdie-charcoal/[0.06] text-birdie-charcoal/45" : ""
      } ${day.past ? "opacity-35" : ""} ${active ? "ring-2 ring-birdie-charcoal/70 ring-offset-1" : ""}`}
      style={style}
    >
      {n}
    </button>
  );
}

function DayDetail({ day }) {
  const d = parse(day.date);
  const label = `${day.dow}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
  if (!day.events.length) {
    return (
      <div>
        <span className="font-semibold text-birdie-charcoal">{label}</span>
        <span className="text-birdie-charcoal/55"> · {day.past ? "Nothing was booked" : "Open"}</span>
      </div>
    );
  }
  return (
    <div className="space-y-0.5">
      <div className="font-semibold text-birdie-charcoal">{label}</div>
      {day.events.map((e, i) => (
        <div key={i} className="text-xs text-birdie-charcoal/70">
          {e.time} · {e.customer || "—"} · {e.title}{" "}
          <span className={e.status === "booked" ? "text-birdie-green font-semibold" : "text-birdie-charcoal/50"}>
            ({e.status === "booked" ? "booked" : "held"})
          </span>
        </div>
      ))}
    </div>
  );
}

function Legend({ swatch, children }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {swatch}
      {children}
    </span>
  );
}
